import { createQueryKeys } from '@lukemorales/query-key-factory';
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryOptions,
} from '@tanstack/react-query';
import { ProductCategoryApi } from 'api';
import type { ProductCategory } from 'api/methods/ProductCategoryApi';

type Return = {
  status: number;
  message: string;
  data: ProductCategory | ProductCategory[];
};

/**
 * [ProductCategory] Query key 생성
 * --
 */
export const productCategoryQueryKeys = createQueryKeys('productCategory', {
  getProductCategory: (
    idx: number,
    params?: { [key: string]: string | number }
  ) => [idx, params],
  getAllCategories: (params?: { [key: string]: string | number }) => [params],
  getProductCategories: (params?: { [key: string]: string | number }) => [
    'products',
    params,
  ],
  getMenuCategories: (params?: { [key: string]: string | number }) => [
    'menus',
    params,
  ],
});

/**
 * [ProductCategory] 카테고리 전체 조회
 * --
 */
export const useGetAllCategories = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: productCategoryQueryKeys.getAllCategories(params).queryKey,
    queryFn: () => ProductCategoryApi.getProductCategories(params),
    ...options,
  });
  return data;
};

/**
 * [ProductCategory] 상품 카테고리 전체 조회
 * --
 */
export const useGetProductCategories = (
  params?: {
    [key: string]: string | number;
  },
  options?: UseQueryOptions<any, any>
) => {
  const data = useQuery({
    queryKey: productCategoryQueryKeys.getProductCategories(params).queryKey,
    queryFn: () => ProductCategoryApi.getProductCategories(params),
    select: (result) => {
      const { status, message, data } = result;

      const productCategories = data?.filter(
        (item: ProductCategory) => item.type === 'PRODUCT'
      );

      return {
        status: status,
        message: message,
        data: productCategories,
      };
    },
    ...options,
  });
  return data;
};

/**
 * [ProductCategory] 메뉴 카테고리 전체 조회
 * --
 */
export const useGetMenuCategories = (params?: {
  [key: string]: string | number;
}) => {
  const data = useQuery({
    queryKey: productCategoryQueryKeys.getProductCategories(params).queryKey,
    queryFn: () => ProductCategoryApi.getProductCategories(params),
    select: (result) => {
      const { status, message, data } = result;

      const productCategories = data?.filter(
        (item: ProductCategory) => item.type === 'MENU'
      );

      return {
        status: status,
        message: message,
        data: productCategories,
      };
    },
  });
  return data;
};

/**
 * [ProductCategory] 상품 카테고리 상세 조회
 * --
 */
export const useGetProductCategory = (
  idx: number,
  params?: { [key: string]: string | number }
) => {
  const data = useQuery({
    queryKey: productCategoryQueryKeys.getProductCategory(idx, params).queryKey,
    queryFn: () => ProductCategoryApi.getProductCategory(idx, params),
  });

  return data;
};

/**
 * [ProductCategory] 상품 카테고리 생성
 * --
 */
export const useCreateProductCategory = (body: ProductCategory) => {
  const queryClient = useQueryClient();

  const { mutateAsync: createProductCategory, ...result } = useMutation({
    mutationFn: (variables: ProductCategory) =>
      ProductCategoryApi.createProductCategory(variables),
    onSuccess: (data, variables) => {
      const queryCache = queryClient.getQueryCache();

      // productCategory로 저장된 모든 queryKey 가져오기
      const queries = queryCache.findAll({
        queryKey: ['productCategory'],
      });

      // 캐싱된 데이터 업데이트
      const { data: resultData } = data;
      queries.forEach((_item) => {
        queryClient.setQueryData(_item.queryKey, (prev: Return) => {
          if (!prev) {
            return prev;
          }

          const { status, message, data } = prev;

          let newData;
          if (Array.isArray(data)) {
            newData = [...data, resultData];
          } else {
            newData = resultData;
          }

          return {
            status,
            message,
            data: newData,
          };
        });
      });
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { createProductCategory, ...result };
};

/**
 * [ProductCategory] 상품 카테고리 수정
 * --
 */
export const useUpdateProductCategory = (body: ProductCategory) => {
  const { mutateAsync: updateProductCategory, ...result } = useMutation({
    mutationFn: (variables: ProductCategory) =>
      ProductCategoryApi.updateProductCategory(variables),
    onSuccess: (data, variables) => {},
    onError: (error) => {
      console.error(error);
    },
  });

  return { updateProductCategory, ...result };
};

/**
 * [ProductCategory] 상품 카테고리 삭제
 * --
 */
export const useDeleteProductCategory = (idx: number) => {
  const { mutateAsync: deleteProductCategory, ...result } = useMutation({
    mutationFn: (variables: number) =>
      ProductCategoryApi.deleteProductCategory(variables),
    onSuccess: (data, variables) => {
      console.log(data, variables);
    },
    onError: (error) => {
      console.error(error);
    },
  });

  return { deleteProductCategory, ...result };
};

/**
 * [ProductCategory] 상품 카테고리 수정
 * --
 */
export const useUpsertProductCategory = () => {
  const { mutateAsync: upsertProductCategory, ...result } = useMutation({
    mutationFn: (variables: ProductCategory[]) =>
      ProductCategoryApi.upsertProductCategory(variables),
  });

  return { upsertProductCategory, ...result };
};
